// BD Dashboard - metric labels, colours and formatters.
// Mirrors blocks-backend/controllers/landmark/bdMetrics.js.

export const POINT_TYPES = [
  { type: "TAP", key: "taps", label: "TAPs", short: "TAP", color: "#2563eb" },
  { type: "Client Meeting", key: "clientMeetings", label: "Client Meetings", short: "Client Mtg", color: "#0ea5e9" },
  { type: "PP - Client Meeting", key: "ppClientMeetings", label: "PP Client Meetings", short: "PP Mtg", color: "#67e8f9" },
  { type: "Curate Attendee", key: "curateAttendees", label: "Curate Attendees", short: "Curate", color: "#8b5cf6" },
  { type: "LMV", key: "lmv", label: "LMV", short: "LMV", color: "#f59e0b" },
  { type: "New Client Signing", key: "newClientSignings", label: "New Client Signings", short: "New Client", color: "#10b981" },
  { type: "Closed Deal", key: "closedDeals", label: "Closed Deals", short: "Closed", color: "#059669" },
];

export const POINT_TYPE_COLORS = POINT_TYPES.reduce((acc, t) => {
  acc[t.type] = t.color;
  return acc;
}, {});

export const TEAM_COLORS = {
  "Team 1 - IT": "#2563eb",
  "Team 2 - Industrial Tech": "#f59e0b",
  "Team 3 - Digital Media": "#8b5cf6",
  "Team 4 - Cybersecurity": "#10b981",
  "Client Acquisition Team": "#ec4899",
};

export const TEAM_SHORT_NAMES = {
  "Team 1 - IT": "IT",
  "Team 2 - Industrial Tech": "Industrial Tech",
  "Team 3 - Digital Media": "Digital Media",
  "Team 4 - Cybersecurity": "Cybersecurity",
  "Client Acquisition Team": "Client Acquisition",
};

export const teamColor = (team) => TEAM_COLORS[team] || "#64748b";
export const teamShortName = (team) => TEAM_SHORT_NAMES[team] || team || "Unassigned";

// Primary KPI definitions shown in the KPI strip. `format` is one of
// number | currency | points. `metric` (defaults to key) resolves the data
// source shown in the card's SourceTip (see meta.sources from the API);
// `hint` is only a fallback while meta is loading.
export const PRIMARY_KPIS = [
  {
    key: "pointValue",
    label: "Workplan points",
    format: "points",
    hint: "Sum of BD Workplan point values in the period ('All BD WP Points' report)",
  },
  {
    key: "bluesBooked",
    label: "Client meetings booked (Blues)",
    format: "number",
    hint: "Client Meeting + PP - Client Meeting BD Workplan Points in the period ('All BD WP Points' report)",
  },
  { key: "tapsBooked", label: "TAPs booked", format: "number", hint: "TAP BD Workplan Points in the period ('All BD WP Points' report)" },
  { key: "meetingsHappened", label: "Meetings held", format: "number", hint: "TAP call reports + other call reports (Salesforce Tasks) by Activity Date" },
  { key: "lmvHeld", label: "LMV meetings held", format: "number", hint: "LMV BD Workplan Points whose Call Taken On date is in the period ('All BD WP Points' report)" },
  { key: "eventsHeld", label: "Events held", format: "number", hint: "BD events ('All BD Events' report, Scenario 1 (BD), no 'Curate' in the name) with an Event Start Date in the period and on or before today" },
  {
    key: "bdDealsClosed",
    label: "Deals closed",
    format: "number",
    hint: "Closed Won 'Business Development' opportunities by Close Date ('All BD Closed Deals' report)",
  },
  {
    key: "newClients",
    label: "Signed clients",
    format: "number",
    hint: "Closed Won 'Client Acquisition - BD' opportunities by Close Date ('All BD Signed Clients' report)",
  },
  { key: "curateDinners", label: "Signed dinners", format: "number", hint: "Closed Won 'Curate' opportunities ('Closed Won Curate' report)" },
  {
    key: "liveClients",
    label: "Clients",
    format: "number",
    hint: "Live BD clients (Retainer + PPL + Commission only); as of today for current periods, or active during the period for past ranges",
  },
];

// Sub-line for the Clients KPI: the three contract-type segments.
export const clientSegmentsSub = (kpis) =>
  kpis ? `${formatNumber(kpis.retainedClients)} retainer · ${formatNumber(kpis.pplClients)} PPL · ${formatNumber(kpis.commissionClients)} commission` : null;

// Client segments (by Contract Type) shared by the Clients card and the
// team / member pages. Mirrors bdMetrics.BD_CLIENT_SEGMENT_CONTRACT_TYPES.
export const CLIENT_SEGMENTS = [
  { id: "retainer", label: "Retainer", metric: "retainedClients", color: "#2563eb", note: "Month-to-Month / Term" },
  { id: "ppl", label: "PPL", metric: "pplClients", color: "#f59e0b", note: "Per Meeting" },
  { id: "commission", label: "Commission only", metric: "commissionClients", color: "#8492a6", note: "Commission / Referral" },
];
export const clientSegment = (id) => CLIENT_SEGMENTS.find((s) => s.id === id) || null;

// Sub-line for the client KPIs - same snapshot vs historical wording as
// bdClientsSub, without the created-in-period count.
export const clientsSub = (period) => {
  const mode = period && period.clients ? period.clients.mode : "snapshot";
  return mode === "historical" ? "active during period" : "as of today";
};

// Sub-line for the BD clients KPI: the API tells us whether the count is a
// "right now" snapshot (Last week / periods including today) or historical.
export const bdClientsSub = (period, created) => {
  const mode = period && period.clients ? period.clients.mode : "snapshot";
  const prefix = mode === "historical" ? "active during period" : "as of today";
  return created === null || created === undefined ? prefix : `${prefix} · ${created} created in period`;
};

export const METRIC_LABELS = {
  points: "Points",
  pointValue: "Point value",
  meetingsBooked: "Meetings booked",
  bluesBooked: "Client meetings booked (Blues)",
  tapsBooked: "TAPs booked",
  meetingsReceived: "Meetings received",
  meetingsScheduled: "Scheduled",
  meetingsHappened: "Meetings held",
  tapsHeld: "TAPs held",
  callsHeld: "Other calls held",
  meetingsUpcoming: "Upcoming",
  meetingsNoShow: "No shows",
  meetingsLost: "Lost",
  revenue: "Revenue",
  wonDeals: "Closed-won deals",
  retainerFeeSum: "Retainer fees",
  contractValueSum: "Contract value",
  bdDealsClosed: "BD deals closed",
  bdDealsAmount: "BD deals amount",
  newClients: "New clients",
  dealsClosedTotal: "Deals + clients closed",
  curateDinners: "Signed dinners",
  bdClients: "BD clients",
  bdClientsNew: "New BD client records",
  liveClients: "Clients",
  retainedClients: "Retainer clients",
  pplClients: "PPL clients",
  commissionClients: "Commission-only clients",
  eventsHeld: "Events held",
  lmvHeld: "LMV meetings held",
  pipelineCount: "Open opps",
  pipelineAmount: "Open opp value",
  activeClients: "Current clients",
  taps: "TAPs",
  clientMeetings: "Client meetings",
  ppClientMeetings: "PP client meetings",
  curateAttendees: "Curate attendees",
  lmv: "LMV",
  newClientSignings: "New clients",
  closedDeals: "Closed deals",
};

const numberFmt = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const decimalFmt = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });
const currencyFmt = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});
const compactCurrencyFmt = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  maximumFractionDigits: 1,
});
const compactNumberFmt = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});

export const formatNumber = (n) => numberFmt.format(Number(n) || 0);
export const formatDecimal = (n) => decimalFmt.format(Number(n) || 0);
export const formatCurrency = (n) => currencyFmt.format(Number(n) || 0);
export const formatCompactCurrency = (n) => compactCurrencyFmt.format(Number(n) || 0);
export const formatCompactNumber = (n) => compactNumberFmt.format(Number(n) || 0);

export const formatMetric = (value, format) => {
  if (format === "currency") return formatCompactCurrency(value);
  if (format === "currencyFull") return formatCurrency(value);
  if (format === "points") return formatDecimal(value);
  return formatNumber(value);
};

export const formatPct = (pct, { sign = true } = {}) => {
  if (pct === null || pct === undefined || Number.isNaN(Number(pct))) return "—";
  const n = Number(pct);
  const prefix = sign && n > 0 ? "+" : "";
  return `${prefix}${decimalFmt.format(Math.round(n * 10) / 10)}%`;
};

// Money for table cells: zero / null renders as a muted dash.
export const moneyOrDash = (n, compact = true) => {
  const v = Number(n) || 0;
  if (!v) return "—";
  return compact ? formatCompactCurrency(v) : formatCurrency(v);
};

// Salesforce org timezone - used to render datetime fields (e.g. the raw
// Booked On timestamp). Date-only fields and the API's calendar-day fields
// (bookedOnDay, accreditedDate) are UTC midnight and are rendered in UTC.
export const ORG_TZ = "America/New_York";

export const formatDate = (value, opts = {}) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    timeZone: opts.timeZone || "UTC",
    month: "short",
    day: "numeric",
    year: opts.year === false ? undefined : "numeric",
  });
};

// Date for a point row's selected period field. bookedOnDay / accreditedDate
// are calendar days (UTC midnight); the raw bookedOn timestamp is shown in ET.
export const formatPointDate = (row, field, opts = {}) =>
  formatDate(row && row[field], { ...opts, timeZone: field === "bookedOn" ? ORG_TZ : "UTC" });

export const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const relativeTime = (value) => {
  if (!value) return "never";
  const diff = Date.now() - new Date(value).getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
};

// Formats a trend bucket key ("2026-09", "2026-W36", "2026-09-04") for an axis.
export const formatBucket = (bucket, granularity) => {
  if (!bucket) return "";
  if (granularity === "month" || /^\d{4}-\d{2}$/.test(bucket)) {
    const [y, m] = bucket.split("-");
    const d = new Date(Date.UTC(Number(y), Number(m) - 1, 1));
    return d.toLocaleDateString("en-US", { timeZone: "UTC", month: "short", year: "2-digit" });
  }
  if (/W/.test(bucket)) {
    return bucket.replace(/^\d{4}-/, "");
  }
  return formatDate(bucket, { year: false });
};

export const initials = (name = "") =>
  String(name)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");

export const MEETING_STATUS_TONE = {
  Received: "neutral",
  "First Attempt": "neutral",
  "Second Attempt": "neutral",
  "Third Attempt": "neutral",
  Scheduled: "info",
  Rescheduling: "warning",
  "Already Happened": "success",
  "Lost - Unresponsive": "danger",
  "Kicked Back": "danger",
  "Rejected by Client": "danger",
  "Rejected by Customer": "danger",
};

// Opportunity record types used by the BD reports -> short label + tone.
export const OPP_RECORD_TYPE_META = {
  "Business Development": { short: "BD deal", tone: "success", report: "bdDeals" },
  "Client Acquisition - BD": { short: "New client", tone: "info", report: "newClients" },
  Curate: { short: "Curate", tone: "neutral", report: "curateWon" },
};

export const CALL_REPORT_KIND_META = {
  tap: { label: "TAP call report", short: "TAP", tone: "info", metric: "tapsHeld" },
  call: { label: "Call report", short: "Call", tone: "neutral", metric: "callsHeld" },
};

export const CLIENT_STATUS_TONE = {
  Active: "success",
  "Pre-Sign": "info",
  "On Hold": "warning",
  Paused: "warning",
  Needs_Renewal: "warning",
  "Wind Down": "danger",
  Terminated: "danger",
};

export const healthTone = (score) => {
  if (score === null || score === undefined) return "neutral";
  if (score >= 75) return "success";
  if (score >= 40) return "warning";
  return "danger";
};
